import React, { useCallback, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Header, SubmitBtn, FileViewer, Copyright } from '../../components';
import { makeStyles } from '@material-ui/core/styles';
import AttachFileIcon from '@material-ui/icons/Description';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import fileDownload from 'js-file-download';
import { useApi, useRoutes } from '../../hooks';
import { useAlert } from '../../components/alert-info/use-alert';

const useStyles = makeStyles((theme) => ({
  appBar: {
    width: '100%',
  },
  toolbar: theme.mixins.toolbar,
  container: {
    background: theme.palette.background.default,
    minHeight: '100vh',
  },
  icon: {
    width: 200,
    height: 200,
  },
  content: {
    margin: '1px auto',
    marginTop: theme.spacing(5),

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  btn: {
    marginTop: theme.spacing(2),
    width: 200,
  },
  copyright: {
    marginTop: theme.spacing(5),
  },
}));

export const Files = () => {
  const [open, setOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [images, setImages] = useState<any[]>([]);
  const classes = useStyles();
  const params: any = useParams();
  const { getAttachedPieceByHash } = useApi();
  const { paths, navigate } = useRoutes();
  const { show, AlertTypes } = useAlert();

  const navigateToHome = navigate(paths.home.path);

  const isImage = useMemo(() => {
    const name: string = params?.name || '';
    const extensions = name?.split('.') || [];
    const fileExtension: string = extensions[extensions.length - 1] || '';
    return ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension.toLowerCase());
  }, [params]);

  const getFile = useCallback(() => {
    if (response) return response;
    if (params) {
      const { reportId, name, hash } = params as any;
      const fileResponse = getAttachedPieceByHash({ reportId, name, hash });
      setResponse(fileResponse);
      return fileResponse;
    }
  }, [params, response, getAttachedPieceByHash]);

  const handleOnFileOpen = useCallback(async () => {
    try {
      setOpen(true);
      setImageLoading(true);
      const fileBlob = await getFile();
      const url = window.URL.createObjectURL(fileBlob as Blob);
      setImages([url]);
      setImageLoading(false);
    } catch (error) {
      show({
        message: "Impossible d'afficher l'image",
        severity: AlertTypes.error,
      });
      setImageLoading(false);
    }
  }, [getFile, AlertTypes, show]);

  const handleOnDownload = useCallback(async () => {
    try {
      setDownloading(true);
      const fileBlob = await getFile();
      fileDownload(fileBlob, params?.name || '');
      setDownloading(false);
    } catch (error) {
      show({
        message: 'Impossible de télécharger le document',
        severity: AlertTypes.error,
      });
      setDownloading(false);
    }
  }, [getFile, params, AlertTypes, show]);

  const handleOnFileViewClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <div>
      <Header className={classes.appBar} showTitle onMenuClick={navigateToHome} />
      <div className={classes.container}>
        <div className={classes.toolbar} />
        <div className={classes.content}>
          <AttachFileIcon color="action" fontSize="large" className={classes.icon} />
          <Typography color="textPrimary">{params?.name}</Typography>
          <SubmitBtn
            title="Télécharger"
            variant="contained"
            color="primary"
            isSubmitting={downloading}
            className={classes.btn}
            onClick={handleOnDownload}
          />
          {isImage && (
            <Button color="primary" className={classes.btn} onClick={handleOnFileOpen}>
              Ouvrir
            </Button>
          )}
          <div className={classes.copyright}>
            <Copyright />
          </div>
        </div>
      </div>
      <FileViewer
        open={open}
        images={images}
        onClose={handleOnFileViewClose}
        loading={imageLoading}
      />
    </div>
  );
};
export default Files;
